import React, { useMemo, useState } from 'react';
import { useTenantContext } from '../../context/TenantContext';
import { Plus, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';
import { IconSelector } from './IconSelector';

interface PublicFooterConfigProps {
  formData: any;
  handleConfigChange: (path: string, value: any) => void;
}

// Quelques polices communes (elles seront utilisées si déjà chargées par le thème)
const FONT_FAMILIES = [
  'system-ui', 'Inter', 'Montserrat', 'Poppins', 'Roboto', 'Merriweather', 'Lora', 'Nunito', 'Open Sans'
];

const ORIENTATIONS = [
  { value: 'to-b', label: 'Bas (to-b)' },
  { value: 'to-t', label: 'Haut (to-t)' },
  { value: 'to-r', label: 'Droite (to-r)' },
  { value: 'to-l', label: 'Gauche (to-l)' },
  { value: 'to-tr', label: 'Haut-Droite (to-tr)' },
  { value: 'to-br', label: 'Bas-Droite (to-br)' },
  { value: 'to-tl', label: 'Haut-Gauche (to-tl)' },
  { value: 'to-bl', label: 'Bas-Gauche (to-bl)' },
];

const PublicFooterConfig: React.FC<PublicFooterConfigProps> = ({ formData, handleConfigChange }) => {
  const { setPreviewValue } = useTenantContext();

  // Valeurs par défaut (style "présentation"): fond bleu nuit, titres en dégradé, corps clair
  const defaults = useMemo(() => ({
    publicFooter: {
      borderTop: { enabled: true, color: 'rgba(255,255,255,0.08)' },
      background: {
        mode: 'solid', // 'transparent' | 'solid' | 'gradient'
        solidColor: '#08062b',
        gradient: {
          orientation: 'to-br',
          color1: '#08062b',
          opacity1: 0.95,
          color2: '#008aef',
          opacity2: 0.40,
        },
      },
      typography: {
        heading: {
          mode: 'gradient', // 'gradient' | 'solid'
          color: '#ffffff',
          gradientFrom: '#008aef',
          gradientTo: '#007acc',
          fontFamily: 'Inter',
          fontSize: 20, // en px (sera appliqué aux h3)
          fontWeight: 700,
          italic: false,
          uppercase: true,
          letterSpacing: 0,
          lineHeight: 1.2,
        },
        body: {
          color: '#d1d5db',
          fontFamily: 'Inter',
          fontSize: 14,
          fontWeight: 400,
          italic: false,
          letterSpacing: 0,
          lineHeight: 1.6,
        },
        link: {
          color: '#d1d5db',
          hoverColor: '#ffffff',
          fontSize: 14,
          fontWeight: 500,
          underline: false,
        },
        copyright: {
          color: '#d1d5db',
          fontSize: 13,
          fontWeight: 500,
        },
        button: {
          backgroundColor: '#fe8d17',
          textColor: '#ffffff',
          hoverBackgroundColor: '#e47c15',
          hoverTextColor: '#ffffff',
          fontWeight: 700,
          radius: 12,
          paddingV: 12,
          paddingH: 16,
        },
      },
      icons: {
        contact: {
          phone: { name: 'Phone', color: '#008aef', size: 20 },
          mail: { name: 'Mail', color: '#008aef', size: 20 },
          location: { name: 'MapPin', color: '#008aef', size: 20 },
        },
        social: {
          style: 'pill', // 'pill' | 'square' | 'ghost'
          color: '#ffffff',
          hoverBg: 'rgba(0,138,239,0.2)'
        },
        copyright: {
          logo: { name: 'Server', color: '#008aef', size: 16 },
          developer: { name: 'Wrench', color: '#fe8d17', size: 12 }
        }
      },
      decorations: {
        topLineColor: 'rgba(254,141,23,0.8)',
        particles: {
          enabled: true,
          color1: 'rgba(254,141,23,0.08)',
          color2: 'rgba(0,138,239,0.05)',
          color3: 'rgba(255,255,255,0.05)'
        }
      }
    }
  }), []);

  const footer = formData.config.publicFooter || defaults.publicFooter;

  const setValue = (path: string, value: any) => {
    handleConfigChange(`publicFooter.${path}`, value);
    setPreviewValue(`publicFooter.${path}`, value);
  };

  // Disposition & Colonnes - valeurs par défaut et états locaux d'édition
  const layoutDefaults = { columnsPerRow: { sm: 2, lg: 3, xl: 4 }, designTemplate: 'default' };
  const layout = formData.config.publicFooter?.layout || layoutDefaults;
  
  // Couleurs configurables des templates
  const primaryColor = layout.templateColors?.primary || '#008aef';
  const secondaryColor = layout.templateColors?.secondary || '#fe8d17';

  const defaultColumns = useMemo(() => ([
    { id: 'identity', type: 'identity', enabled: true },
    { id: 'links', type: 'links', enabled: true, title: 'LIENS UTILES', items: [
      { id: 'home', label: 'Accueil', href: '/', icon: 'Home', iconColor: primaryColor },
      { id: 'services', label: 'Nos services', href: '/#services', icon: 'Wrench', iconColor: secondaryColor },
      { id: 'about', label: 'À propos de SIS', href: '/about', icon: 'Info', iconColor: primaryColor },
      { id: 'contact', label: 'Contactez-nous', href: '/contact', icon: 'Headset', iconColor: secondaryColor },
    ]},
    { id: 'contact', type: 'contact', enabled: true, title: 'CONTACT & RÉSEAUX', settings: { showPhone: true, showEmail: true, showAddress: true } },
    { id: 'newsletter', type: 'newsletter', enabled: true, title: 'RESTEZ INFORMÉ', description: 'Abonnez-vous à notre newsletter pour recevoir des conseils techniques et des offres exclusives.', submitLabel: "S'abonner" },
  ]), [primaryColor, secondaryColor]);

  const columnsList = formData.config.publicFooter?.columns?.list || defaultColumns;
  const [newColType, setNewColType] = useState<'custom_links' | 'rich_text' | 'legal_links' | 'partners_logos' | 'payment_methods' | 'opening_hours' | 'locations' | 'embed_map' | 'social_links' | 'cta_button'>('custom_links');
  const [newColTitle, setNewColTitle] = useState('Nouvelle colonne');
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  return (
    <div className="space-y-8">
      <h3 className="text-lg font-medium text-white border-b border-white/10 pb-2">Footer Public</h3>

      {/* Bordure supérieure */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h4 className="text-base font-semibold text-white mb-4">Bordure supérieure</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={footer.borderTop?.enabled ?? defaults.publicFooter.borderTop.enabled}
              onChange={(e) => setValue('borderTop.enabled', e.target.checked)}
              className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20"
            />
            <span className="text-gray-200 text-sm">Afficher la bordure</span>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-2">Couleur de la bordure</label>
            <div className="flex space-x-2">
              <input
                type="color"
                value={footer.borderTop?.color || defaults.publicFooter.borderTop.color}
                onChange={(e) => setValue('borderTop.color', e.target.value)}
                className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
              />
              <input
                type="text"
                value={footer.borderTop?.color || defaults.publicFooter.borderTop.color}
                onChange={(e) => setValue('borderTop.color', e.target.value)}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Fond du footer */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h4 className="text-base font-semibold text-white mb-4">Fond du footer</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Mode</label>
            <select
              value={footer.background?.mode || 'solid'}
              onChange={(e) => setValue('background.mode', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
              style={{ colorScheme: 'dark' }}
            >
              <option value="transparent" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Transparent</option>
              <option value="solid" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Couleur pleine</option>
              <option value="gradient" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Dégradé</option>
            </select>
          </div>

          {(footer.background?.mode === 'solid') && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-2">Couleur de fond</label>
              <div className="flex space-x-2">
                <input
                  type="color"
                  value={footer.background?.solidColor || defaults.publicFooter.background.solidColor}
                  onChange={(e) => setValue('background.solidColor', e.target.value)}
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                />
                <input
                  type="text"
                  value={footer.background?.solidColor || defaults.publicFooter.background.solidColor}
                  onChange={(e) => setValue('background.solidColor', e.target.value)}
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                />
              </div>
            </div>
          )}

          {(footer.background?.mode === 'gradient') && (
            <div className="md:col-span-2 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Orientation</label>
                  <select
                    value={footer.background?.gradient?.orientation || 'to-br'}
                    onChange={(e) => setValue('background.gradient.orientation', e.target.value)}
                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                    style={{ colorScheme: 'dark' }}
                  >
                    {ORIENTATIONS.map(o => (
                      <option key={o.value} value={o.value} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Couleur 1</label>
                  <div className="flex space-x-2">
                    <input
                      type="color"
                      value={footer.background?.gradient?.color1 || defaults.publicFooter.background.gradient.color1}
                      onChange={(e) => setValue('background.gradient.color1', e.target.value)}
                      className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                    />
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.05}
                      value={(footer.background?.gradient?.opacity1 ?? defaults.publicFooter.background.gradient.opacity1)}
                      onChange={(e) => setValue('background.gradient.opacity1', parseFloat(e.target.value))}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Couleur 2</label>
                  <div className="flex space-x-2">
                    <input
                      type="color"
                      value={footer.background?.gradient?.color2 || defaults.publicFooter.background.gradient.color2}
                      onChange={(e) => setValue('background.gradient.color2', e.target.value)}
                      className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                    />
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.05}
                      value={(footer.background?.gradient?.opacity2 ?? defaults.publicFooter.background.gradient.opacity2)}
                      onChange={(e) => setValue('background.gradient.opacity2', parseFloat(e.target.value))}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Disposition & Colonnes */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h4 className="text-base font-semibold text-white mb-4">Disposition & Colonnes</h4>

        {/* Nombre de colonnes par breakpoint */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Colonnes (sm)</label>
            <select
              value={layout.columnsPerRow?.sm ?? 2}
              onChange={(e) => setValue('layout.columnsPerRow.sm', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
              style={{ colorScheme: 'dark' }}
            >
              {[1,2,3,4,5,6].map(n => (<option key={n} value={n} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{n}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Colonnes (lg)</label>
            <select
              value={layout.columnsPerRow?.lg ?? 3}
              onChange={(e) => setValue('layout.columnsPerRow.lg', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
              style={{ colorScheme: 'dark' }}
            >
              {[1,2,3,4,5,6].map(n => (<option key={n} value={n} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{n}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Colonnes (xl)</label>
            <select
              value={layout.columnsPerRow?.xl ?? 4}
              onChange={(e) => setValue('layout.columnsPerRow.xl', parseInt(e.target.value))}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
              style={{ colorScheme: 'dark' }}
            >
              {[1,2,3,4,5,6].map(n => (<option key={n} value={n} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{n}</option>))}
            </select>
          </div>
        </div>

        {/* Template de design des colonnes */}
        <div className="mb-6 space-y-3 bg-black/20 border border-white/10 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">Template de design</label>
          <select
            value={layout.designTemplate || 'default'}
            onChange={(e) => setValue('layout.designTemplate', e.target.value)}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
            style={{ colorScheme: 'dark' }}
          >
            <optgroup label="🎨 Classiques" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
              <option value="default" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>✨ Par défaut (Glassmorphism)</option>
              <option value="card" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>🃏 Carte élégante</option>
              <option value="neon" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>💡 Néon lumineux</option>
              <option value="minimal" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>📏 Minimal</option>
              <option value="gradient" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>🌈 Dégradé coloré</option>
              <option value="modern" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>🎯 Moderne accent</option>
              <option value="glass" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>🔮 Verre givré</option>
              <option value="dark" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>🌑 Sombre élégant</option>
            </optgroup>
            <optgroup label="🎨 Créatifs & Formes" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
              <option value="hexagon" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>⬡ Hexagones futuristes</option>
              <option value="diamond" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>💎 Diamants brillants</option>
              <option value="circle" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>⭕ Cercles organiques</option>
              <option value="origami" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>📐 Pli origami</option>
            </optgroup>
            <optgroup label="🎭 Bordures & Décorations" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
              <option value="zigzag" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>⚡ Zigzag dynamique</option>
              <option value="wavy" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>🌊 Ondulations fluides</option>
              <option value="torn" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>📄 Papier déchiré</option>
              <option value="stitched" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>🧵 Coutures artisanales</option>
            </optgroup>
            <optgroup label="🚀 Thématiques" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
              <option value="cyberpunk" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>🤖 Cyberpunk néon</option>
              <option value="vintage" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>📻 Vintage rétro</option>
              <option value="artdeco" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>🎭 Art déco élégant</option>
              <option value="brutalist" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>🏗️ Brutaliste moderne</option>
            </optgroup>
            <optgroup label="⚡ Avant-gardistes" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
              <option value="holographic" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>🌈 Holographique</option>
              <option value="glitch" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>📺 Effet glitch</option>
              <option value="morphism" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>🔘 Skeuomorphisme</option>
              <option value="abstract" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>🎨 Formes abstraites</option>
            </optgroup>
          </select>
          <div className="text-xs text-gray-400">
            Le template s'applique à toutes les colonnes. Personnalisez les couleurs ci-dessous.
          </div>
        </div>

        {/* Couleurs des templates */}
        <div className="mb-6 space-y-3 bg-black/20 border border-white/10 rounded-lg p-4">
          <label className="block text-sm font-medium text-gray-300 mb-2">🎨 Couleurs des templates</label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Couleur primaire (bleu)</label>
              <div className="flex space-x-2">
                <input
                  type="color"
                  value={layout.templateColors?.primary || '#008aef'}
                  onChange={(e) => setValue('layout.templateColors.primary', e.target.value)}
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                />
                <input
                  type="text"
                  value={layout.templateColors?.primary || '#008aef'}
                  onChange={(e) => setValue('layout.templateColors.primary', e.target.value)}
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                  placeholder="#008aef"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Couleur secondaire (orange)</label>
              <div className="flex space-x-2">
                <input
                  type="color"
                  value={layout.templateColors?.secondary || '#fe8d17'}
                  onChange={(e) => setValue('layout.templateColors.secondary', e.target.value)}
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
                />
                <input
                  type="text"
                  value={layout.templateColors?.secondary || '#fe8d17'}
                  onChange={(e) => setValue('layout.templateColors.secondary', e.target.value)}
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                  placeholder="#fe8d17"
                />
              </div>
            </div>
          </div>
          <div className="text-xs text-gray-400">
            Ces couleurs sont utilisées dans les bordures, effets et dégradés des templates de colonnes.
          </div>
        </div>

        {/* Largeurs personnalisées (en % ou fraction) */}
        <div className="mb-6 space-y-3 bg-black/20 border border-white/10 rounded-lg p-4">
          <label className="flex items-center gap-3 text-sm text-gray-300">
            <input
              type="checkbox"
              checked={!!layout.useCustomWidths}
              onChange={(e)=> setValue('layout.useCustomWidths', e.target.checked)}
              className="h-5 w-5 rounded border-gray-600 text-gray-500 bg-black/20"/>
            Activer les largeurs personnalisées (ex: 25, 25% ou 1/3)
          </label>
          {layout.useCustomWidths && (
            <>
              <div className="text-xs text-gray-400">
                {(() => {
                  const sum = (columnsList || []).reduce((acc:number,c:any)=> acc + (Number(c.widthPercent) || 0), 0);
                  const ok = Math.abs(sum - 100) < 0.5;
                  return (
                    <span>Somme des largeurs: <span className={ok ? 'text-green-400' : 'text-yellow-300'}>{sum.toFixed(2)}%</span> {!ok && '(conseillé: 100%)'}</span>
                  );
                })()}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={()=>{
                    const list = [...columnsList];
                    const eq = 100 / Math.max(list.length, 1);
                    setValue('columns.list', list.map((c:any)=> ({ ...c, widthPercent: eq })));
                  }}
                  className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md border border-white/10 text-sm"
                >Distribuer équitablement</button>
                <button
                  type="button"
                  onClick={()=>{
                    let list = [...columnsList];
                    const sum = list.reduce((acc:number,c:any)=> acc + (Number(c.widthPercent) || 0), 0);
                    if (sum <= 0) {
                      const eq = 100 / Math.max(list.length, 1);
                      list = list.map((c:any)=> ({ ...c, widthPercent: eq }));
                    } else {
                      const factor = 100 / sum;
                      list = list.map((c:any)=> ({ ...c, widthPercent: (Number(c.widthPercent) || 0) * factor }));
                    }
                    setValue('columns.list', list);
                  }}
                  className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md border border-white/10 text-sm"
                >Normaliser à 100%</button>
              </div>
            </>
          )}
        </div>

        {/* Liste des colonnes */}
        <div className="space-y-4">
          {columnsList.map((col: any, idx: number) => (
            <div key={col.id || idx} className="bg-black/20 border border-white/10 rounded-lg">
              {/* Header ligne */}
              <div className="flex items-center justify-between px-4 py-3">
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => { const list = [...columnsList]; if (idx>0) { const [it] = list.splice(idx,1); list.splice(idx-1,0,it); setValue('columns.list', list); } }} className="p-1 rounded hover:bg-white/10 text-gray-300" title="Monter"><ChevronUp className="w-4 h-4"/></button>
                  <button type="button" onClick={() => { const list = [...columnsList]; if (idx<list.length-1) { const [it] = list.splice(idx,1); list.splice(idx+1,0,it); setValue('columns.list', list); } }} className="p-1 rounded hover:bg-white/10 text-gray-300" title="Descendre"><ChevronDown className="w-4 h-4"/></button>
                  <input type="checkbox" checked={col.enabled !== false} onChange={(e) => { const list = [...columnsList]; list[idx] = { ...col, enabled: e.target.checked }; setValue('columns.list', list); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
                  <span className="text-sm text-gray-300 font-medium">{col.type}</span>
                </div>
                <div className="flex items-center gap-2">
                  {['links','custom_links','contact','newsletter','rich_text','legal_links','partners_logos','payment_methods','opening_hours','locations','embed_map','social_links','cta_button'].includes(col.type) && (
                    <input type="text" placeholder="Titre de la colonne" value={col.title || ''} onChange={(e) => { const list = [...columnsList]; list[idx] = { ...col, title: e.target.value }; setValue('columns.list', list); }} className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-md text-white text-sm" />
                  )}
                  {layout?.useCustomWidths && (
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-400">Larg.</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="25 ou 1/3"
                        value={typeof col.widthPercent === 'number' ? (Number.isFinite(col.widthPercent) ? (Math.round(col.widthPercent * 100) / 100).toFixed(2) : '') : ''}
                        onChange={(e)=>{
                          const raw = e.target.value.trim();
                          let pct: number | null = null;
                          if (raw.endsWith('%')) {
                            const n = parseFloat(raw.replace('%',''));
                            pct = isNaN(n) ? null : n;
                          } else if (raw.includes('/')) {
                            const [a,b] = raw.split('/').map(x=> parseFloat(x));
                            if (!isNaN(a) && !isNaN(b) && b !== 0) pct = (a / b) * 100;
                          } else {
                            const n = parseFloat(raw);
                            pct = isNaN(n) ? null : n;
                          }
                          if (pct !== null && isFinite(pct)) {
                            const bounded = Math.max(0, Math.min(100, pct));
                            const list = [...columnsList];
                            list[idx] = { ...col, widthPercent: bounded };
                            setValue('columns.list', list);
                          }
                        }}
                        className="w-24 px-2 py-1.5 bg-white/5 border border-white/10 rounded-md text-white text-sm"
                      />
                      <span className="text-xs text-gray-400">%</span>
                    </div>
                  )}
                  <button type="button" onClick={() => setExpandedIndex(expandedIndex===idx?null:idx)} className="px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white rounded-md text-sm">{expandedIndex===idx? 'Masquer' : 'Configurer'}</button>
                  {!(col.type==='identity') && (
                    <button type="button" onClick={() => { const list = columnsList.filter((_:any,i:number)=>i!==idx); setValue('columns.list', list); }} className="px-2 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-md border border-red-500/30" title="Supprimer"><Trash2 className="w-4 h-4"/></button>
                  )}
                </div>
              </div>

              {/* Zone d’édition */}
              {expandedIndex === idx && (
                <div className="px-4 pb-4 space-y-4">
                  {col.type === 'identity' && (
                    <div className="space-y-4">
                      <div className="text-gray-300 text-sm font-medium">Configuration de la colonne identité</div>
                      
                      {/* Disposition générale */}
                      <div className="bg-white/5 rounded-lg p-4 space-y-3">
                        <label className="block text-sm text-gray-400 mb-2">Disposition des éléments</label>
                        <div className="grid grid-cols-2 gap-3">
                          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                            <input 
                              type="radio" 
                              name={`layout-${idx}`}
                              value="vertical"
                              checked={(col.settings?.layout || 'vertical') === 'vertical'} 
                              onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), layout: 'vertical' } }; setValue('columns.list', list); }} 
                              className="h-4 w-4 text-gray-500 bg-black/20"
                            />
                            📋 Vertical (empilé)
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                            <input 
                              type="radio" 
                              name={`layout-${idx}`}
                              value="horizontal"
                              checked={col.settings?.layout === 'horizontal'} 
                              onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), layout: 'horizontal' } }; setValue('columns.list', list); }} 
                              className="h-4 w-4 text-gray-500 bg-black/20"
                            />
                            ↔️ Horizontal (côte à côte)
                          </label>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                            <input 
                              type="radio" 
                              name={`align-${idx}`}
                              value="left"
                              checked={col.settings?.alignment === 'left'} 
                              onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), alignment: 'left' } }; setValue('columns.list', list); }} 
                              className="h-4 w-4 text-gray-500 bg-black/20"
                            />
                            ⬅️ Gauche
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                            <input 
                              type="radio" 
                              name={`align-${idx}`}
                              value="center"
                              checked={(col.settings?.alignment || 'center') === 'center'} 
                              onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), alignment: 'center' } }; setValue('columns.list', list); }} 
                              className="h-4 w-4 text-gray-500 bg-black/20"
                            />
                            ↔️ Centre
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                            <input 
                              type="radio" 
                              name={`align-${idx}`}
                              value="right"
                              checked={col.settings?.alignment === 'right'} 
                              onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), alignment: 'right' } }; setValue('columns.list', list); }} 
                              className="h-4 w-4 text-gray-500 bg-black/20"
                            />
                            ➡️ Droite
                          </label>
                        </div>
                      </div>

                      {/* Ordre des éléments */}
                      <div className="bg-white/5 rounded-lg p-4 space-y-3">
                        <label className="block text-sm text-gray-400 mb-2">Ordre d'affichage</label>
                        <select
                          value={col.settings?.order || 'logo-name-desc'}
                          onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), order: e.target.value } }; setValue('columns.list', list); }}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                          style={{ colorScheme: 'dark' }}
                        >
                          <option value="logo-name-desc" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Logo → Nom → Description</option>
                          <option value="logo-desc-name" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Logo → Description → Nom</option>
                          <option value="name-logo-desc" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Nom → Logo → Description</option>
                          <option value="name-desc-logo" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Nom → Description → Logo</option>
                          <option value="desc-logo-name" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Description → Logo → Nom</option>
                          <option value="desc-name-logo" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Description → Nom → Logo</option>
                        </select>
                      </div>

                      {/* Visibilité des éléments */}
                      <div className="bg-white/5 rounded-lg p-4 space-y-3">
                        <label className="block text-sm text-gray-400 mb-2">Éléments visibles</label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <label className="flex items-center gap-2 text-sm text-gray-300">
                            <input 
                              type="checkbox" 
                              checked={col.settings?.showLogo !== false} 
                              onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), showLogo: e.target.checked } }; setValue('columns.list', list); }} 
                              className="h-5 w-5 rounded border-gray-600 text-gray-500 bg-black/20"
                            />
                            Afficher le logo
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-300">
                            <input 
                              type="checkbox" 
                              checked={col.settings?.showName !== false} 
                              onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), showName: e.target.checked } }; setValue('columns.list', list); }} 
                              className="h-5 w-5 rounded border-gray-600 text-gray-500 bg-black/20"
                            />
                            Afficher le nom
                          </label>
                          <label className="flex items-center gap-2 text-sm text-gray-300">
                            <input 
                              type="checkbox" 
                              checked={col.settings?.showDescription !== false} 
                              onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), showDescription: e.target.checked } }; setValue('columns.list', list); }} 
                              className="h-5 w-5 rounded border-gray-600 text-gray-500 bg-black/20"
                            />
                            Afficher la description
                          </label>
                        </div>
                      </div>

                      {/* Personnalisation du logo */}
                      {col.settings?.showLogo !== false && (
                        <div className="bg-white/5 rounded-lg p-4 space-y-3">
                          <label className="block text-sm text-gray-400 mb-2">Personnalisation du logo</label>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Taille (hauteur en px)</label>
                              <input 
                                type="number" 
                                min="40" 
                                max="200" 
                                value={col.settings?.logoSize || 112} 
                                onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), logoSize: parseInt(e.target.value) } }; setValue('columns.list', list); }} 
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Style</label>
                              <select
                                value={col.settings?.logoStyle || 'rounded'}
                                onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), logoStyle: e.target.value } }; setValue('columns.list', list); }}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                                style={{ colorScheme: 'dark' }}
                              >
                                <option value="rounded" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Arrondi</option>
                                <option value="circle" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Cercle</option>
                                <option value="square" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Carré</option>
                                <option value="none" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Sans bordure</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Personnalisation du nom */}
                      {col.settings?.showName !== false && (
                        <div className="bg-white/5 rounded-lg p-4 space-y-3">
                          <label className="block text-sm text-gray-400 mb-2">Personnalisation du nom</label>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Taille de police (px)</label>
                              <input 
                                type="number" 
                                min="12" 
                                max="48" 
                                value={col.settings?.nameSize || 24} 
                                onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), nameSize: parseInt(e.target.value) } }; setValue('columns.list', list); }} 
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Style</label>
                              <select
                                value={col.settings?.nameStyle || 'gradient'}
                                onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), nameStyle: e.target.value } }; setValue('columns.list', list); }}
                                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                                style={{ colorScheme: 'dark' }}
                              >
                                <option value="gradient" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Dégradé</option>
                                <option value="solid" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Couleur unie</option>
                                <option value="outline" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Contour</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Description */}
                      <div className="bg-white/5 rounded-lg p-4 space-y-3">
                        <label className="block text-sm text-gray-400 mb-2">Texte de description</label>
                        <textarea 
                          rows={3} 
                          value={col.description || 'Votre partenaire pour une transformation numérique réussie. Partenaire technologique au Congo : maintenance, réseaux, développement et support technique.'} 
                          onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, description: e.target.value }; setValue('columns.list', list); }} 
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                          placeholder="Description de votre entreprise..."
                        />
                        <div className="text-xs text-gray-500">Ce texte décrit votre entreprise dans le footer</div>
                      </div>
                    </div>
                  )}

                  {['links','custom_links'].includes(col.type) && (
                    <div className="space-y-3">
                      <div className="text-gray-300 text-sm">Éléments de liens</div>
                      {(col.items || []).map((it: any, j: number) => (
                        <div key={it.id || j} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                          <div className="md:col-span-2">
                            <label className="block text-xs text-gray-400 mb-1">Libellé</label>
                            <input type="text" value={it.label || ''} onChange={(e)=>{ const list = [...columnsList]; const items = [...(col.items||[])]; items[j] = { ...(items[j]||{}), label: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
                          </div>
                          <div className="md:col-span-4">
                            <label className="block text-xs text-gray-400 mb-1">Lien</label>
                            <input type="text" value={it.href || ''} onChange={(e)=>{ const list = [...columnsList]; const items = [...(col.items||[])]; items[j] = { ...(items[j]||{}), href: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs text-gray-400 mb-1">Icône</label>
                            <select value={it.icon || 'Info'} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), icon: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" style={{ colorScheme: 'dark' }}>
                              {['Home','Wrench','Info','Headset','Mail','Phone','MapPin','Code','Server','Building2','Globe','ArrowRight'].map(i => (<option key={i} value={i} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{i}</option>))}
                            </select>
                          </div>
                          <div className="md:col-span-3">
                            <label className="block text-xs text-gray-400 mb-1">Couleur icône</label>
                            <div className="flex space-x-1">
                              <input
                                type="color"
                                value={it.iconColor || primaryColor}
                                onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), iconColor: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }}
                                className="h-9 w-12 rounded border border-white/10 bg-transparent cursor-pointer"
                              />
                              <input
                                type="text"
                                value={it.iconColor || primaryColor}
                                onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), iconColor: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }}
                                className="flex-1 px-2 py-1.5 bg-white/5 border border-white/10 rounded-md text-white text-xs"
                                placeholder={primaryColor}
                              />
                            </div>
                          </div>
                          <div className="md:col-span-1 flex items-end">
                            <button type="button" onClick={()=>{ const list=[...columnsList]; const items=(col.items||[]).filter((_:any,k:number)=>k!==j); list[idx] = { ...col, items }; setValue('columns.list', list); }} className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-md border border-red-500/30"><Trash2 className="w-4 h-4"/></button>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={()=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items.push({ id: `link_${Date.now()}`, label: 'Nouveau lien', href: '/', icon: 'Info', iconColor: primaryColor }); list[idx] = { ...col, items }; setValue('columns.list', list); }} className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md border border-white/10"><Plus className="w-4 h-4 inline mr-2"/>Ajouter un lien</button>
                    </div>
                  )}

                  {col.type === 'contact' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={col.settings?.showPhone !== false} onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), showPhone: e.target.checked } }; setValue('columns.list', list); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 bg-black/20"/>Afficher téléphone</label>
                      <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={col.settings?.showEmail !== false} onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), showEmail: e.target.checked } }; setValue('columns.list', list); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 bg-black/20"/>Afficher email</label>
                      <label className="flex items-center gap-2 text-sm text-gray-300"><input type="checkbox" checked={col.settings?.showAddress !== false} onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, settings: { ...(col.settings||{}), showAddress: e.target.checked } }; setValue('columns.list', list); }} className="h-5 w-5 rounded border-gray-600 text-gray-500 bg-black/20"/>Afficher adresse</label>
                    </div>
                  )}

                  {col.type === 'newsletter' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Description</label>
                        <input type="text" value={col.description || ''} onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, description: e.target.value }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Libellé du bouton</label>
                        <input type="text" value={col.submitLabel || "S'abonner"} onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, submitLabel: e.target.value }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                      </div>
                    </div>
                  )}

                  {col.type === 'rich_text' && (
                    <div>
                      <label className="block text-sm text-gray-400 mb-1">Contenu</label>
                      <textarea rows={4} value={col.content || ''} onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, content: e.target.value }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                    </div>
                  )}

                  {/* LEGAL LINKS */}
                  {col.type === 'legal_links' && (
                    <div className="space-y-3">
                      <div className="text-gray-300 text-sm">Liens juridiques</div>
                      {(col.items || []).map((it: any, j: number) => (
                        <div key={it.id || j} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                          <div className="md:col-span-5">
                            <label className="block text-xs text-gray-400 mb-1">Libellé</label>
                            <input type="text" value={it.label || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), label: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-6">
                            <label className="block text-xs text-gray-400 mb-1">Lien</label>
                            <input type="text" value={it.href || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), href: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-1 flex items-end">
                            <button type="button" onClick={()=>{ const list=[...columnsList]; const items=(col.items||[]).filter((_:any,k:number)=>k!==j); list[idx] = { ...col, items }; setValue('columns.list', list); }} className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-md border border-red-500/30"><Trash2 className="w-4 h-4"/></button>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={()=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items.push({ id: `legal_${Date.now()}`, label: 'Mentions légales', href: '/mentions-legales' }); list[idx] = { ...col, items }; setValue('columns.list', list); }} className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md border border-white/10"><Plus className="w-4 h-4 inline mr-2"/>Ajouter un lien</button>
                    </div>
                  )}

                  {/* PARTNERS LOGOS */}
                  {col.type === 'partners_logos' && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-sm text-gray-400 mb-1">Disposition</label>
                          <select value={col.layout || 'grid'} onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, layout: e.target.value }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" style={{ colorScheme: 'dark' }}>
                            <option value="grid" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Grille</option>
                            <option value="carousel" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Carrousel</option>
                          </select>
                        </div>
                      </div>
                      <div className="text-gray-300 text-sm">Partenaires</div>
                      {(col.items || []).map((it: any, j: number) => (
                        <div key={it.id || j} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                          <div className="md:col-span-3">
                            <label className="block text-xs text-gray-400 mb-1">Nom</label>
                            <input type="text" value={it.name || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), name: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-5">
                            <label className="block text-xs text-gray-400 mb-1">Logo URL</label>
                            <input type="text" placeholder="https://..." value={it.logoUrl || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), logoUrl: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-3">
                            <label className="block text-xs text-gray-400 mb-1">Lien</label>
                            <input type="text" placeholder="https://..." value={it.href || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), href: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-1 flex items-end">
                            <button type="button" onClick={()=>{ const list=[...columnsList]; const items=(col.items||[]).filter((_:any,k:number)=>k!==j); list[idx] = { ...col, items }; setValue('columns.list', list); }} className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-md border border-red-500/30"><Trash2 className="w-4 h-4"/></button>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={()=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items.push({ id: `partner_${Date.now()}`, name: 'Partenaire', logoUrl: '', href: '#' }); list[idx] = { ...col, items }; setValue('columns.list', list); }} className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md border border-white/10"><Plus className="w-4 h-4 inline mr-2"/>Ajouter un partenaire</button>
                    </div>
                  )}

                  {/* PAYMENT METHODS */}
                  {col.type === 'payment_methods' && (
                    <div className="space-y-3">
                      <div className="text-gray-300 text-sm">Moyens de paiement</div>
                      {(col.items || []).map((it: any, j: number) => (
                        <div key={it.id || j} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                          <div className="md:col-span-3">
                            <label className="block text-xs text-gray-400 mb-1">Nom</label>
                            <input type="text" placeholder="Visa, MasterCard, PayPal..." value={it.name || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), name: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-4">
                            <label className="block text-xs text-gray-400 mb-1">Icône (nom)</label>
                            <input type="text" placeholder="visa | mastercard | paypal ... (optionnel)" value={it.icon || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), icon: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-4">
                            <label className="block text-xs text-gray-400 mb-1">Image URL (optionnel)</label>
                            <input type="text" placeholder="https://..." value={it.imageUrl || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), imageUrl: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-1 flex items-end">
                            <button type="button" onClick={()=>{ const list=[...columnsList]; const items=(col.items||[]).filter((_:any,k:number)=>k!==j); list[idx] = { ...col, items }; setValue('columns.list', list); }} className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-md border border-red-500/30"><Trash2 className="w-4 h-4"/></button>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={()=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items.push({ id: `pay_${Date.now()}`, name: 'Visa', icon: 'visa', imageUrl: '' }); list[idx] = { ...col, items }; setValue('columns.list', list); }} className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md border border-white/10"><Plus className="w-4 h-4 inline mr-2"/>Ajouter un moyen de paiement</button>
                    </div>
                  )}

                  {/* OPENING HOURS */}
                  {col.type === 'opening_hours' && (
                    <div className="space-y-3">
                      <div className="text-gray-300 text-sm">Horaires d'ouverture</div>
                      {(() => {
                        const days = col.hours || [
                          { day: 'mon', open: '08:00', close: '18:00' },
                          { day: 'tue', open: '08:00', close: '18:00' },
                          { day: 'wed', open: '08:00', close: '18:00' },
                          { day: 'thu', open: '08:00', close: '18:00' },
                          { day: 'fri', open: '08:00', close: '18:00' },
                          { day: 'sat', open: '09:00', close: '13:00' },
                          { day: 'sun', open: '', close: '' },
                        ];
                        if (!col.hours) { const list=[...columnsList]; list[idx] = { ...col, hours: days }; setValue('columns.list', list); }
                        const label = (d: string) => ({mon:'Lun',tue:'Mar',wed:'Mer',thu:'Jeu',fri:'Ven',sat:'Sam',sun:'Dim'} as any)[d] || d;
                        return (
                          <div className="space-y-2">
                            {days.map((h: any, j: number) => (
                              <div key={j} className="grid grid-cols-12 gap-2 items-center">
                                <div className="col-span-2 text-sm text-gray-300">{label(h.day)}</div>
                                <div className="col-span-4">
                                  <input type="time" value={h.open || ''} onChange={(e)=>{ const list=[...columnsList]; const hours=[...(col.hours||days)]; hours[j] = { ...(hours[j]||{}), open: e.target.value }; list[idx] = { ...col, hours }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                                </div>
                                <div className="col-span-4">
                                  <input type="time" value={h.close || ''} onChange={(e)=>{ const list=[...columnsList]; const hours=[...(col.hours||days)]; hours[j] = { ...(hours[j]||{}), close: e.target.value }; list[idx] = { ...col, hours }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                                </div>
                                <div className="col-span-2">
                                  <button type="button" onClick={()=>{ const list=[...columnsList]; const hours=[...(col.hours||days)]; hours[j] = { ...(hours[j]||{}), open: '', close: '' }; list[idx] = { ...col, hours }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md border border-white/10">Fermé</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* LOCATIONS */}
                  {col.type === 'locations' && (
                    <div className="space-y-3">
                      <div className="text-gray-300 text-sm">Adresses</div>
                      {(col.items || []).map((it: any, j: number) => (
                        <div key={it.id || j} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                          <div className="md:col-span-3">
                            <label className="block text-xs text-gray-400 mb-1">Libellé</label>
                            <input type="text" value={it.label || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), label: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-5">
                            <label className="block text-xs text-gray-400 mb-1">Adresse</label>
                            <input type="text" value={it.address || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), address: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-2">
                            <label className="block text-xs text-gray-400 mb-1">Ville</label>
                            <input type="text" value={it.city || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), city: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-1">
                            <label className="block text-xs text-gray-400 mb-1">Pays</label>
                            <input type="text" value={it.country || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), country: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-1">
                            <label className="block text-xs text-gray-400 mb-1">Map</label>
                            <input type="text" placeholder="https://maps..." value={it.mapUrl || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), mapUrl: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-1 flex items-end">
                            <button type="button" onClick={()=>{ const list=[...columnsList]; const items=(col.items||[]).filter((_:any,k:number)=>k!==j); list[idx] = { ...col, items }; setValue('columns.list', list); }} className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-md border border-red-500/30"><Trash2 className="w-4 h-4"/></button>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={()=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items.push({ id: `loc_${Date.now()}`, label: 'Siège', address: '', city: '', country: '', mapUrl: '' }); list[idx] = { ...col, items }; setValue('columns.list', list); }} className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md border border-white/10"><Plus className="w-4 h-4 inline mr-2"/>Ajouter une localisation</button>
                    </div>
                  )}

                  {/* EMBED MAP */}
                  {col.type === 'embed_map' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="md:col-span-2">
                        <label className="block text-sm text-gray-400 mb-1">Embed URL (Google Maps)</label>
                        <input type="text" placeholder="https://www.google.com/maps/embed?..." value={col.embedUrl || ''} onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, embedUrl: e.target.value }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Hauteur (px)</label>
                        <input type="number" min={200} max={800} value={col.height ?? 260} onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, height: Number(e.target.value || 260) }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                      </div>
                    </div>
                  )}

                  {/* SOCIAL LINKS */}
                  {col.type === 'social_links' && (
                    <div className="space-y-3">
                      <div className="text-gray-300 text-sm">Réseaux sociaux</div>
                      {(col.items || []).map((it: any, j: number) => (
                        <div key={it.id || j} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center">
                          <div className="md:col-span-3">
                            <label className="block text-xs text-gray-400 mb-1">Réseau</label>
                            <select value={it.network || 'facebook'} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), network: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" style={{ colorScheme: 'dark' }}>
                              {['facebook','instagram','linkedin','x','youtube','tiktok'].map(n => (<option key={n} value={n} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{n}</option>))}
                            </select>
                          </div>
                          <div className="md:col-span-8">
                            <label className="block text-xs text-gray-400 mb-1">Lien</label>
                            <input type="text" placeholder="https://..." value={it.href || ''} onChange={(e)=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items[j] = { ...(items[j]||{}), href: e.target.value }; list[idx] = { ...col, items }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                          </div>
                          <div className="md:col-span-1 flex items-end">
                            <button type="button" onClick={()=>{ const list=[...columnsList]; const items=(col.items||[]).filter((_:any,k:number)=>k!==j); list[idx] = { ...col, items }; setValue('columns.list', list); }} className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-md border border-red-500/30"><Trash2 className="w-4 h-4"/></button>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={()=>{ const list=[...columnsList]; const items=[...(col.items||[])]; items.push({ id: `soc_${Date.now()}`, network: 'facebook', href: '' }); list[idx] = { ...col, items }; setValue('columns.list', list); }} className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md border border-white/10"><Plus className="w-4 h-4 inline mr-2"/>Ajouter un réseau</button>
                    </div>
                  )}

                  {/* CTA BUTTON */}
                  {col.type === 'cta_button' && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Texte du bouton</label>
                        <input type="text" value={col.text || 'Nous contacter'} onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, text: e.target.value }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Lien</label>
                        <input type="text" value={col.href || '/contact'} onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, href: e.target.value }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Variante</label>
                        <select value={col.variant || 'primary'} onChange={(e)=>{ const list=[...columnsList]; list[idx] = { ...col, variant: e.target.value }; setValue('columns.list', list); }} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" style={{ colorScheme: 'dark' }}>
                          <option value="primary" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Primaire</option>
                          <option value="secondary" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Secondaire</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Ajouter une nouvelle colonne */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Type de colonne</label>
            <select value={newColType} onChange={(e)=> setNewColType(e.target.value as any)} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" style={{ colorScheme: 'dark' }}>
              <option value="custom_links" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Liens personnalisés</option>
              <option value="legal_links" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Liens juridiques</option>
              <option value="partners_logos" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Partenaires (logos)</option>
              <option value="social_links" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Réseaux sociaux</option>
              <option value="newsletter" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Newsletter</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Titre</label>
            <input type="text" value={newColTitle} onChange={(e)=> setNewColTitle(e.target.value)} className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"/>
          </div>
          <div>
            <button type="button" onClick={()=>{
              const list = [...columnsList];
              const id = `col_${Date.now()}`;
              if (newColType === 'custom_links') {
                list.push({ id, type: 'custom_links', enabled: true, title: newColTitle || 'Liens', items: [] });
              } else if (newColType === 'legal_links') {
                list.push({ id, type: 'legal_links', enabled: true, title: newColTitle || 'Juridique', items: [
                  { id: `legal_${Date.now()}_1`, label: 'Mentions légales', href: '/mentions-legales' },
                  { id: `legal_${Date.now()}_2`, label: 'Politique de confidentialité', href: '/politique-confidentialite' },
                  { id: `legal_${Date.now()}_3`, label: 'Conditions d’utilisation', href: '/conditions' },
                ] });
              } else if (newColType === 'partners_logos') {
                list.push({ id, type: 'partners_logos', enabled: true, title: newColTitle || 'Partenaires', layout: 'grid', items: [] });
              } else if (newColType === 'payment_methods') {
                list.push({ id, type: 'payment_methods', enabled: true, title: newColTitle || 'Paiements', items: [] });
              } else if (newColType === 'opening_hours') {
                list.push({ id, type: 'opening_hours', enabled: true, title: newColTitle || 'Horaires', hours: [
                  { day: 'mon', open: '08:00', close: '18:00' },
                  { day: 'tue', open: '08:00', close: '18:00' },
                  { day: 'wed', open: '08:00', close: '18:00' },
                  { day: 'thu', open: '08:00', close: '18:00' },
                  { day: 'fri', open: '08:00', close: '18:00' },
                  { day: 'sat', open: '09:00', close: '13:00' },
                  { day: 'sun', open: '', close: '' },
                ] });
              } else if (newColType === 'locations') {
                list.push({ id, type: 'locations', enabled: true, title: newColTitle || 'Nos adresses', items: [] });
              } else if (newColType === 'embed_map') {
                list.push({ id, type: 'embed_map', enabled: true, title: newColTitle || 'Nous situer', embedUrl: '', height: 260 });
              } else if (newColType === 'social_links') {
                list.push({ id, type: 'social_links', enabled: true, title: newColTitle || 'Réseaux sociaux', items: [] });
              } else if (newColType === 'cta_button') {
                list.push({ id, type: 'cta_button', enabled: true, title: newColTitle || 'Contact', text: 'Nous contacter', href: '/contact', variant: 'primary' });
              } else {
                list.push({ id, type: 'rich_text', enabled: true, title: newColTitle || 'Informations', content: '' });
              }
              setValue('columns.list', list);
            }} className="w-full md:w-auto px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-md border border-white/10"><Plus className="w-4 h-4 inline mr-2"/>Ajouter la colonne</button>
          </div>
        </div>
      </div>

      {/* Typographies */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h4 className="text-base font-semibold text-white mb-4">Typographies</h4>

        {/* Titres */}
        <div className="space-y-3 mb-6">
          <h5 className="text-sm font-medium text-gray-300">Titres des sections</h5>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Mode</label>
              <select
                value={footer.typography?.heading?.mode || defaults.publicFooter.typography.heading.mode}
                onChange={(e) => setValue('typography.heading.mode', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                style={{ colorScheme: 'dark' }}
              >
                <option value="gradient" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Dégradé (par défaut)</option>
                <option value="solid" style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>Couleur solide</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Police</label>
              <select
                value={footer.typography?.heading?.fontFamily || defaults.publicFooter.typography.heading.fontFamily}
                onChange={(e) => setValue('typography.heading.fontFamily', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                style={{ colorScheme: 'dark' }}
              >
                {FONT_FAMILIES.map(f => (<option key={f} value={f} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{f}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Taille (px)</label>
              <input type="number" min={10} max={48}
                value={footer.typography?.heading?.fontSize ?? defaults.publicFooter.typography.heading.fontSize}
                onChange={(e) => setValue('typography.heading.fontSize', Number(e.target.value || 20))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Graisse</label>
              <select
                value={footer.typography?.heading?.fontWeight ?? defaults.publicFooter.typography.heading.fontWeight}
                onChange={(e) => setValue('typography.heading.fontWeight', Number(e.target.value))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                style={{ colorScheme: 'dark' }}
              >
                {[400,500,600,700,800].map(w => (<option key={w} value={w} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{w}</option>))}
              </select>
            </div>
            <div className="flex items-center space-x-3">
              <input type="checkbox"
                checked={!!footer.typography?.heading?.italic}
                onChange={(e) => setValue('typography.heading.italic', e.target.checked)}
                className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
              <span className="text-gray-200 text-sm">Italique</span>
            </div>
            <div className="flex items-center space-x-3">
              <input type="checkbox"
                checked={!!footer.typography?.heading?.uppercase}
                onChange={(e) => setValue('typography.heading.uppercase', e.target.checked)}
                className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
              <span className="text-gray-200 text-sm">Majuscules</span>
            </div>
          </div>

          {/* Couleurs titres */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {(footer.typography?.heading?.mode === 'solid') && (
              <div className="md:col-span-1">
                <label className="block text-sm text-gray-400 mb-1">Couleur</label>
                <div className="flex space-x-2">
                  <input type="color"
                    value={footer.typography?.heading?.color || defaults.publicFooter.typography.heading.color}
                    onChange={(e) => setValue('typography.heading.color', e.target.value)}
                    className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                  <input type="text"
                    value={footer.typography?.heading?.color || defaults.publicFooter.typography.heading.color}
                    onChange={(e) => setValue('typography.heading.color', e.target.value)}
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
                </div>
              </div>
            )}
            {(footer.typography?.heading?.mode !== 'solid') && (
              <>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Dégradé - From</label>
                  <div className="flex space-x-2">
                    <input type="color"
                      value={footer.typography?.heading?.gradientFrom || defaults.publicFooter.typography.heading.gradientFrom}
                      onChange={(e) => setValue('typography.heading.gradientFrom', e.target.value)}
                      className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                    <input type="text"
                      value={footer.typography?.heading?.gradientFrom || defaults.publicFooter.typography.heading.gradientFrom}
                      onChange={(e) => setValue('typography.heading.gradientFrom', e.target.value)}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Dégradé - To</label>
                  <div className="flex space-x-2">
                    <input type="color"
                      value={footer.typography?.heading?.gradientTo || defaults.publicFooter.typography.heading.gradientTo}
                      onChange={(e) => setValue('typography.heading.gradientTo', e.target.value)}
                      className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                    <input type="text"
                      value={footer.typography?.heading?.gradientTo || defaults.publicFooter.typography.heading.gradientTo}
                      onChange={(e) => setValue('typography.heading.gradientTo', e.target.value)}
                      className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Corps */}
        <div className="space-y-3 mb-6">
          <h5 className="text-sm font-medium text-gray-300">Texte courant</h5>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Couleur</label>
              <div className="flex space-x-2">
                <input type="color"
                  value={footer.typography?.body?.color || defaults.publicFooter.typography.body.color}
                  onChange={(e) => setValue('typography.body.color', e.target.value)}
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text"
                  value={footer.typography?.body?.color || defaults.publicFooter.typography.body.color}
                  onChange={(e) => setValue('typography.body.color', e.target.value)}
                  className="w-20 flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Police</label>
              <select
                value={footer.typography?.body?.fontFamily || defaults.publicFooter.typography.body.fontFamily}
                onChange={(e) => setValue('typography.body.fontFamily', e.target.value)}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                style={{ colorScheme: 'dark' }}
              >
                {FONT_FAMILIES.map(f => (<option key={f} value={f} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{f}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Taille (px)</label>
              <input type="number" min={10} max={24}
                value={footer.typography?.body?.fontSize ?? defaults.publicFooter.typography.body.fontSize}
                onChange={(e) => setValue('typography.body.fontSize', Number(e.target.value || 14))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Graisse</label>
              <select
                value={footer.typography?.body?.fontWeight ?? defaults.publicFooter.typography.body.fontWeight}
                onChange={(e) => setValue('typography.body.fontWeight', Number(e.target.value))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                style={{ colorScheme: 'dark' }}
              >
                {[300,400,500,600].map(w => (<option key={w} value={w} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{w}</option>))}
              </select>
            </div>
          </div>
        </div>

        {/* Liens */}
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-gray-300">Liens</h5>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Couleur</label>
              <div className="flex space-x-2">
                <input type="color"
                  value={footer.typography?.link?.color || defaults.publicFooter.typography.link.color}
                  onChange={(e) => setValue('typography.link.color', e.target.value)}
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text"
                  value={footer.typography?.link?.color || defaults.publicFooter.typography.link.color}
                  onChange={(e) => setValue('typography.link.color', e.target.value)}
                  className="w-20 flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Couleur hover</label>
              <div className="flex space-x-2">
                <input type="color"
                  value={footer.typography?.link?.hoverColor || defaults.publicFooter.typography.link.hoverColor}
                  onChange={(e) => setValue('typography.link.hoverColor', e.target.value)}
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text"
                  value={footer.typography?.link?.hoverColor || defaults.publicFooter.typography.link.hoverColor}
                  onChange={(e) => setValue('typography.link.hoverColor', e.target.value)}
                  className="w-20 flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Taille (px)</label>
              <input type="number" min={10} max={24}
                value={footer.typography?.link?.fontSize ?? defaults.publicFooter.typography.link.fontSize}
                onChange={(e) => setValue('typography.link.fontSize', Number(e.target.value || 14))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Graisse</label>
              <select
                value={footer.typography?.link?.fontWeight ?? defaults.publicFooter.typography.link.fontWeight}
                onChange={(e) => setValue('typography.link.fontWeight', Number(e.target.value))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                style={{ colorScheme: 'dark' }}
              >
                {[400,500,600,700].map(w => (<option key={w} value={w} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{w}</option>))}
              </select>
            </div>
          </div>
          <div className="flex items-center space-x-3 mt-2">
            <input type="checkbox"
              checked={!!footer.typography?.link?.underline}
              onChange={(e) => setValue('typography.link.underline', e.target.checked)}
              className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20" />
            <span className="text-gray-200 text-sm">Souligner</span>
          </div>
        </div>
      </div>

      {/* Icônes & éléments */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h4 className="text-base font-semibold text-white mb-4">Icônes & éléments</h4>

        {/* Icônes de contact */}
        <div className="space-y-3 mb-6">
          <h5 className="text-sm font-medium text-gray-300">Icônes de contact</h5>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Téléphone */}
            <div className="space-y-2">
              <label className="block text-sm text-gray-400">Téléphone - Icône</label>
              <IconSelector
                value={footer.icons?.contact?.phone?.name || 'Phone'}
                onChange={(value) => setValue('icons.contact.phone.name', value)}
                preset="contactPhone"
              />
              <label className="block text-sm text-gray-400">Couleur</label>
              <div className="flex space-x-2">
                <input type="color"
                  value={footer.icons?.contact?.phone?.color || '#008aef'}
                  onChange={(e) => setValue('icons.contact.phone.color', e.target.value)}
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text"
                  value={footer.icons?.contact?.phone?.color || '#008aef'}
                  onChange={(e) => setValue('icons.contact.phone.color', e.target.value)}
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
              </div>
              <label className="block text-sm text-gray-400">Taille (px)</label>
              <input type="number" min={12} max={28}
                value={footer.icons?.contact?.phone?.size ?? 20}
                onChange={(e) => setValue('icons.contact.phone.size', Number(e.target.value || 20))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
            </div>
            {/* Email */}
            <div className="space-y-2">
              <label className="block text-sm text-gray-400">Email - Icône</label>
              <IconSelector
                value={footer.icons?.contact?.mail?.name || 'Mail'}
                onChange={(value) => setValue('icons.contact.mail.name', value)}
                preset="contactEmail"
              />
              <label className="block text-sm text-gray-400">Couleur</label>
              <div className="flex space-x-2">
                <input type="color"
                  value={footer.icons?.contact?.mail?.color || '#008aef'}
                  onChange={(e) => setValue('icons.contact.mail.color', e.target.value)}
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text"
                  value={footer.icons?.contact?.mail?.color || '#008aef'}
                  onChange={(e) => setValue('icons.contact.mail.color', e.target.value)}
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
              </div>
              <label className="block text-sm text-gray-400">Taille (px)</label>
              <input type="number" min={12} max={28}
                value={footer.icons?.contact?.mail?.size ?? 20}
                onChange={(e) => setValue('icons.contact.mail.size', Number(e.target.value || 20))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
            </div>
            {/* Localisation */}
            <div className="space-y-2">
              <label className="block text-sm text-gray-400">Localisation - Icône</label>
              <IconSelector
                value={footer.icons?.contact?.location?.name || 'MapPin'}
                onChange={(value) => setValue('icons.contact.location.name', value)}
                preset="contactLocation"
              />
              <label className="block text-sm text-gray-400">Couleur</label>
              <div className="flex space-x-2">
                <input type="color"
                  value={footer.icons?.contact?.location?.color || '#008aef'}
                  onChange={(e) => setValue('icons.contact.location.color', e.target.value)}
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text"
                  value={footer.icons?.contact?.location?.color || '#008aef'}
                  onChange={(e) => setValue('icons.contact.location.color', e.target.value)}
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
              </div>
              <label className="block text-sm text-gray-400">Taille (px)</label>
              <input type="number" min={12} max={28}
                value={footer.icons?.contact?.location?.size ?? 20}
                onChange={(e) => setValue('icons.contact.location.size', Number(e.target.value || 20))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
            </div>
          </div>
        </div>

        {/* Icônes du copyright */}
        <div className="space-y-3 mb-6">
          <h5 className="text-sm font-medium text-gray-300">Icônes du copyright</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Logo copyright */}
            <div className="space-y-2">
              <label className="block text-sm text-gray-400">Logo copyright - Icône</label>
              <IconSelector
                value={footer.icons?.copyright?.logo?.name || 'Server'}
                onChange={(value) => setValue('icons.copyright.logo.name', value)}
                preset="copyrightLogo"
              />
              <label className="block text-sm text-gray-400">Couleur</label>
              <div className="flex space-x-2">
                <input type="color"
                  value={footer.icons?.copyright?.logo?.color || '#008aef'}
                  onChange={(e) => setValue('icons.copyright.logo.color', e.target.value)}
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text"
                  value={footer.icons?.copyright?.logo?.color || '#008aef'}
                  onChange={(e) => setValue('icons.copyright.logo.color', e.target.value)}
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
              </div>
              <label className="block text-sm text-gray-400">Taille (px)</label>
              <input type="number" min={12} max={24}
                value={footer.icons?.copyright?.logo?.size ?? 16}
                onChange={(e) => setValue('icons.copyright.logo.size', Number(e.target.value || 16))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
            </div>
            {/* Icône développeur */}
            <div className="space-y-2">
              <label className="block text-sm text-gray-400">Développeur - Icône</label>
              <IconSelector
                value={footer.icons?.copyright?.developer?.name || 'Wrench'}
                onChange={(value) => setValue('icons.copyright.developer.name', value)}
                preset="copyrightDeveloper"
              />
              <label className="block text-sm text-gray-400">Couleur</label>
              <div className="flex space-x-2">
                <input type="color"
                  value={footer.icons?.copyright?.developer?.color || '#fe8d17'}
                  onChange={(e) => setValue('icons.copyright.developer.color', e.target.value)}
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text"
                  value={footer.icons?.copyright?.developer?.color || '#fe8d17'}
                  onChange={(e) => setValue('icons.copyright.developer.color', e.target.value)}
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
              </div>
              <label className="block text-sm text-gray-400">Taille (px)</label>
              <input type="number" min={10} max={20}
                value={footer.icons?.copyright?.developer?.size ?? 12}
                onChange={(e) => setValue('icons.copyright.developer.size', Number(e.target.value || 12))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
            </div>
          </div>
        </div>

        {/* Newsletter / Bouton */}
        <div className="space-y-3">
          <h5 className="text-sm font-medium text-gray-300">Bouton de newsletter</h5>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Fond</label>
              <div className="flex space-x-2">
                <input type="color"
                  value={footer.typography?.button?.backgroundColor || defaults.publicFooter.typography.button.backgroundColor}
                  onChange={(e) => setValue('typography.button.backgroundColor', e.target.value)}
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text"
                  value={footer.typography?.button?.backgroundColor || defaults.publicFooter.typography.button.backgroundColor}
                  onChange={(e) => setValue('typography.button.backgroundColor', e.target.value)}
                  className="w-20 flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Texte</label>
              <div className="flex space-x-2">
                <input type="color"
                  value={footer.typography?.button?.textColor || defaults.publicFooter.typography.button.textColor}
                  onChange={(e) => setValue('typography.button.textColor', e.target.value)}
                  className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer" />
                <input type="text"
                  value={footer.typography?.button?.textColor || defaults.publicFooter.typography.button.textColor}
                  onChange={(e) => setValue('typography.button.textColor', e.target.value)}
                  className="w-20 flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Graisse</label>
              <select
                value={footer.typography?.button?.fontWeight ?? defaults.publicFooter.typography.button.fontWeight}
                onChange={(e) => setValue('typography.button.fontWeight', Number(e.target.value))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white"
                style={{ colorScheme: 'dark' }}
              >
                {[500,600,700,800].map(w => (<option key={w} value={w} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>{w}</option>))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Rayon (px)</label>
              <input type="number" min={0} max={24}
                value={footer.typography?.button?.radius ?? defaults.publicFooter.typography.button.radius}
                onChange={(e) => setValue('typography.button.radius', Number(e.target.value || 12))}
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Padding V/H (px)</label>
              <div className="grid grid-cols-2 gap-2">
                <input type="number" min={6} max={24}
                  value={footer.typography?.button?.paddingV ?? defaults.publicFooter.typography.button.paddingV}
                  onChange={(e) => setValue('typography.button.paddingV', Number(e.target.value || 12))}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
                <input type="number" min={6} max={32}
                  value={footer.typography?.button?.paddingH ?? defaults.publicFooter.typography.button.paddingH}
                  onChange={(e) => setValue('typography.button.paddingH', Number(e.target.value || 16))}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Décorations */}
      <div className="bg-white/5 rounded-lg p-6 border border-white/10">
        <h4 className="text-base font-semibold text-white mb-4">Décorations</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Ligne supérieure (couleur)</label>
            <div className="flex space-x-2">
              <input
                type="color"
                value={footer.decorations?.topLineColor || defaults.publicFooter.decorations.topLineColor}
                onChange={(e) => setValue('decorations.topLineColor', e.target.value)}
                className="h-10 w-10 rounded border border-white/10 bg-transparent cursor-pointer"
              />
              <input
                type="text"
                value={footer.decorations?.topLineColor || defaults.publicFooter.decorations.topLineColor}
                onChange={(e) => setValue('decorations.topLineColor', e.target.value)}
                className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-gray-500"
              />
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={footer.decorations?.particles?.enabled ?? true}
              onChange={(e) => setValue('decorations.particles.enabled', e.target.checked)}
              className="h-5 w-5 rounded border-gray-600 text-gray-500 focus:ring-gray-500 bg-black/20"
            />
            <span className="text-gray-200 text-sm">Bulles décoratives</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicFooterConfig;
